/** @module Collection filters: the chip row with the active ones, and the sheet to set them. */
import { SlidersHorizontal, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { LocationSelect } from "@/components/location-select";
import { Sheet } from "@/components/ui/overlays";
import {
  CONDITIONS,
  EMPTY,
  type Filters,
  LANGUAGES,
  RARITIES,
} from "@/features/collection/collection-filters";
import { useTags } from "@/features/collection/tags";
import { useLocations } from "@/lib/locations";

type Update = (change: (current: Filters) => Filters) => void;

function Choice({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="chip" aria-pressed={pressed} onClick={onClick}>
      {children}
    </button>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="t-label pb-2">{label}</legend>
      <div className="flex flex-wrap gap-2">{children}</div>
    </fieldset>
  );
}

/** An active filter: the label opens the sheet, the cross clears just this one. */
function ActiveChip({
  label,
  onOpen,
  onClear,
}: {
  label: string;
  onOpen: () => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  return (
    <span className="chip chip-on gap-1 pr-1">
      <button type="button" onClick={onOpen}>
        {label}
      </button>
      <button
        type="button"
        className="grid size-7 place-items-center rounded-full"
        aria-label={t("collection.filter.clearOne", { label })}
        onClick={onClear}
      >
        <X />
      </button>
    </span>
  );
}

export function FilterRow({
  filters,
  update,
  grouping,
  onGrouping,
}: {
  filters: Filters;
  update: Update;
  grouping: "variant" | "copy";
  onGrouping: (next: "variant" | "copy") => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const tags = useTags();
  const locations = useLocations();
  const show = () => {
    setOpen(true);
  };

  const active: { key: string; label: string; clear: (current: Filters) => Filters }[] = [];
  if (filters.language)
    active.push({
      key: "language",
      label: `${t("collection.language")}: ${filters.language.toUpperCase()}`,
      clear: (current) => ({ ...current, language: null }),
    });
  if (filters.condition)
    active.push({
      key: "condition",
      label: `${t("collection.condition")}: ${filters.condition}`,
      clear: (current) => ({ ...current, condition: null }),
    });
  if (filters.rarity)
    active.push({
      key: "rarity",
      label: t(`collection.rarity.${filters.rarity}`),
      clear: (current) => ({ ...current, rarity: null }),
    });
  for (const id of filters.tags) {
    const tag = tags.data?.find((one) => one.id === id);
    active.push({
      key: `tag-${id}`,
      label: tag?.name ?? t("collection.tags"),
      clear: (current) => ({ ...current, tags: current.tags.filter((one) => one !== id) }),
    });
  }
  if (filters.purchase)
    active.push({
      key: "purchase",
      label: t(`purchase.filter.${filters.purchase}`),
      clear: (current) => ({ ...current, purchase: null }),
    });
  if (filters.location)
    active.push({
      key: "location",
      label:
        filters.location === "none"
          ? t("locations.without")
          : (locations.data?.items.find((one) => one.id === filters.location)?.path ??
            t("locations.label")),
      clear: (current) => ({ ...current, location: null }),
    });
  if (filters.status === "archived")
    active.push({
      key: "status",
      label: t("collection.archived"),
      clear: (current) => ({ ...current, status: "active" }),
    });

  return (
    <>
      <div className="flex min-w-0 flex-wrap items-center gap-[6px]">
        <button type="button" className="chip" onClick={show}>
          <SlidersHorizontal />
          {t("collection.filter.open")}
          {active.length > 0 ? <span className="badge badge-count">{active.length}</span> : null}
        </button>
        {active.map((entry) => (
          <ActiveChip
            key={entry.key}
            label={entry.label}
            onOpen={show}
            onClear={() => {
              update(entry.clear);
            }}
          />
        ))}
        {/* What the list shows, as the design says it: grouped by variant. */}
        <button type="button" className="t-sm t-muted hidden px-2 md:inline" onClick={show}>
          {grouping === "variant" ? t("collection.group.byVariant") : t("collection.group.byCopy")}
        </button>
      </div>
      <Sheet
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        label={t("collection.filter.title")}
      >
        <div className="flex flex-col gap-5">
          <h2 className="t-h3">{t("collection.filter.title")}</h2>
          <Section label={t("collection.language")}>
            {LANGUAGES.map((language) => (
              <Choice
                key={language}
                pressed={filters.language === language}
                onClick={() => {
                  update((current) => ({
                    ...current,
                    language: current.language === language ? null : language,
                  }));
                }}
              >
                {language.toUpperCase()}
              </Choice>
            ))}
          </Section>
          <Section label={t("collection.condition")}>
            {CONDITIONS.map((condition) => (
              <Choice
                key={condition}
                pressed={filters.condition === condition}
                onClick={() => {
                  update((current) => ({
                    ...current,
                    condition: current.condition === condition ? null : condition,
                  }));
                }}
              >
                {condition}
              </Choice>
            ))}
          </Section>
          <Section label={t("collection.rarityLabel")}>
            {RARITIES.map((rarity) => (
              <Choice
                key={rarity}
                pressed={filters.rarity === rarity}
                onClick={() => {
                  update((current) => ({
                    ...current,
                    rarity: current.rarity === rarity ? null : rarity,
                  }));
                }}
              >
                {t(`collection.rarity.${rarity}`)}
              </Choice>
            ))}
          </Section>
          <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">{t("locations.label")}</legend>
            <LocationSelect
              value={filters.location === "none" ? null : filters.location}
              noneLabel={t("locations.any")}
              onChange={(location) => {
                update((current) => ({ ...current, location }));
              }}
            />
            <Choice
              pressed={filters.location === "none"}
              onClick={() => {
                update((current) => ({
                  ...current,
                  location: current.location === "none" ? null : "none",
                }));
              }}
            >
              {t("locations.without")}
            </Choice>
          </fieldset>
          <Section label={t("purchase.filter.label")}>
            {(["with", "without"] as const).map((purchase) => (
              <Choice
                key={purchase}
                pressed={filters.purchase === purchase}
                onClick={() => {
                  update((current) => ({
                    ...current,
                    purchase: current.purchase === purchase ? null : purchase,
                  }));
                }}
              >
                {t(`purchase.filter.${purchase}`)}
              </Choice>
            ))}
          </Section>
          {(tags.data ?? []).length > 0 ? (
            <Section label={t("collection.tags")}>
              {(tags.data ?? []).map((tag) => (
                <Choice
                  key={tag.id}
                  pressed={filters.tags.includes(tag.id)}
                  onClick={() => {
                    update((current) => ({
                      ...current,
                      tags: current.tags.includes(tag.id)
                        ? current.tags.filter((id) => id !== tag.id)
                        : [...current.tags, tag.id],
                    }));
                  }}
                >
                  {tag.name}
                </Choice>
              ))}
            </Section>
          ) : null}
          <Section label={t("collection.group.label")}>
            <Choice
              pressed={grouping === "variant"}
              onClick={() => {
                onGrouping("variant");
              }}
            >
              {t("collection.group.variant")}
            </Choice>
            <Choice
              pressed={grouping === "copy"}
              onClick={() => {
                onGrouping("copy");
              }}
            >
              {t("collection.group.copy")}
            </Choice>
          </Section>
          <Section label={t("collection.filter.status")}>
            <Choice
              pressed={filters.status === "active"}
              onClick={() => {
                update((current) => ({ ...current, status: "active" }));
              }}
            >
              {t("collection.filter.active")}
            </Choice>
            <Choice
              pressed={filters.status === "archived"}
              onClick={() => {
                update((current) => ({ ...current, status: "archived" }));
              }}
            >
              {t("collection.archived")}
            </Choice>
          </Section>
          <div className="row-between pt-2">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                update((current) => ({ ...EMPTY, query: current.query }));
              }}
            >
              {t("collection.clearFilters")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t("collection.filter.done")}
            </button>
          </div>
        </div>
      </Sheet>
    </>
  );
}
